import React, { useState, useRef } from 'react';
import axios from 'axios';
import './DeckImport.css';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

interface MatchedCard {
  cardId:        string;
  name:          string;
  setCode:       string;
  setName:       string;
  manaCost:      string;
  rarity:        string;
  price:         number | null;
  imageSmall:    string;
  quantity:      number;
  foil:          boolean;
  sideboard:     boolean;
  importName:    string;
  matchStrategy: string;
}

interface UnmatchedCard {
  importName: string;
  setCode:    string | null;
  cardNumber: string | null;
  quantity:   number;
  reason:     string;
}

interface PreviewResult {
  matched:        MatchedCard[];
  unmatched:      UnmatchedCard[];
  matchedCount:   number;
  unmatchedCount: number;
  totalRows:      number;
}

const RARITY_COLOR: Record<string, string> = {
  mythic: '#e8954a', rare: '#c8a45a', uncommon: '#8ab4c8', common: '#9a9aaa',
};

interface Props {
  deckId:   number;
  deckName: string;
  onDone:   () => void;
  onClose:  () => void;
}

export default function DeckImport({ deckId, deckName, onDone, onClose }: Props) {
  const [step,       setStep]       = useState<'input'|'preview'|'done'>('input');
  const [format,     setFormat]     = useState<'auto'|'tcgplayer'|'manual'>('auto');
  const [sideboard,  setSideboard]  = useState(false);
  const [content,    setContent]    = useState('');
  const [preview,    setPreview]    = useState<PreviewResult | null>(null);
  const [selected,   setSelected]   = useState<Set<string>>(new Set());
  const [loading,    setLoading]    = useState(false);
  const [result,     setResult]     = useState<{ added: number; skipped: number } | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setContent(ev.target?.result as string || '');
      if (file.name.toLowerCase().includes('tcg') ||
          file.name.endsWith('.csv') ||
          file.name.endsWith('.tsv') ||
          file.name.endsWith('.txt')) {
        setFormat('tcgplayer');
      }
    };
    reader.readAsText(file);
  };

  const handlePreview = async () => {
    if (!content.trim()) { setError('Please paste or upload a card list.'); return; }
    setLoading(true); setError(null);
    try {
      const res = await axios.post(
        `${API}/decks/${deckId}/import/preview`,
        { content, format, sideboard }
      );
      setPreview(res.data);
      setSelected(new Set(res.data.matched.map((_: MatchedCard, i: number) => String(i))));
      setStep('preview');
    } catch (e) {
      setError('Failed to parse card list. Please check the format and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setLoading(true); setError(null);
    // Pass foil flag through to backend
    const cards = preview.matched
      .filter((_: MatchedCard, i: number) => selected.has(String(i)))
      .map((c: MatchedCard) => ({
        cardId:    c.cardId,
        quantity:  c.quantity,
        sideboard: c.sideboard,
        foil:      c.foil,
      }));

    try {
      const res = await axios.post(
        `${API}/decks/${deckId}/import/confirm`,
        { cards, sideboard }
      );
      setResult(res.data);
      setStep('done');
      onDone();
    } catch (e) {
      setError('Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCard  = (idx: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const selectAll   = () => setSelected(new Set(preview?.matched.map((_: MatchedCard, i: number) => String(i)) || []));
  const deselectAll = () => setSelected(new Set());

  return (
    <div className="deck-import-overlay" onClick={onClose}>
      <div className="deck-import-modal" onClick={e => e.stopPropagation()}>

        <div className="di-header">
          <div className="di-title">
            Import Cards
            <span className="di-deck-name">→ {deckName}</span>
          </div>
          <button className="di-close" onClick={onClose}>✕</button>
        </div>

        {/* ── STEP 1: INPUT ── */}
        {step === 'input' && (
          <div className="di-body">
            <div className="di-format-row">
              <label className="di-label">Format</label>
              <div className="di-format-btns">
                {(['auto','tcgplayer','manual'] as const).map(f => (
                  <button key={f}
                          className={`di-fmt-btn ${format===f?'active':''}`}
                          onClick={() => setFormat(f)}>
                    {f === 'auto'      ? '🔍 Auto-detect'
                     : f === 'tcgplayer' ? '📱 TCGPlayer TSV / CSV'
                     : '✏️ Manual (Name | Set | #)'}
                  </button>
                ))}
              </div>
            </div>

            <div className="di-format-hint">
              {format === 'tcgplayer' && (
                <span>Paste or upload a TCGPlayer export file (tab-separated TSV or CSV). Foil cards are detected automatically from the Printing column.</span>
              )}
              {format === 'manual' && (
                <span>One card per line: <code>4 Lightning Bolt | M11 | 146</code><br/>
                Or just: <code>4 Lightning Bolt</code> (matches best printing)</span>
              )}
              {format === 'auto' && (
                <span>Format will be detected automatically from the content.</span>
              )}
            </div>

            <div className="di-upload-row">
              <button className="di-upload-btn" onClick={() => fileRef.current?.click()}>
                📁 Upload file
              </button>
              <input ref={fileRef} type="file" accept=".csv,.tsv,.txt"
                     style={{ display: 'none' }} onChange={handleFile} />
              <span className="di-or">or paste below</span>
            </div>

            <textarea
              className="di-textarea"
              placeholder={format === 'manual'
                ? "4 Lightning Bolt | M11 | 146\n2 Counterspell | 7ED | 63\n..."
                : "Paste TCGPlayer export content here..."}
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={12}
            />

            <div className="di-sideboard-row">
              <label className="di-check-label">
                <input type="checkbox" checked={sideboard}
                       onChange={e => setSideboard(e.target.checked)} />
                Import as sideboard cards
              </label>
            </div>

            {error && <div className="di-error">{error}</div>}

            <div className="di-actions">
              <button className="di-btn-cancel" onClick={onClose}>Cancel</button>
              <button className="di-btn-primary" onClick={handlePreview}
                      disabled={loading || !content.trim()}>
                {loading ? 'Parsing…' : 'Preview Import →'}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: PREVIEW ── */}
        {step === 'preview' && preview && (
          <div className="di-body">
            <div className="di-preview-summary">
              <span className="di-stat matched">{preview.matchedCount} matched</span>
              {preview.unmatchedCount > 0 && (
                <span className="di-stat unmatched">{preview.unmatchedCount} unmatched</span>
              )}
              <span className="di-stat total">of {preview.totalRows} total</span>
              <div className="di-select-btns">
                <button className="di-sel-btn" onClick={selectAll}>Select all</button>
                <button className="di-sel-btn" onClick={deselectAll}>None</button>
              </div>
            </div>

            {preview.matched.length > 0 && (
              <div className="di-section">
                <div className="di-section-label">✅ Matched — select cards to import</div>
                <div className="di-card-list">
                  {preview.matched.map((card, i) => (
                    <div key={i}
                         className={`di-card-row ${selected.has(String(i)) ? 'selected' : ''}`}
                         onClick={() => toggleCard(String(i))}>
                      <div className="di-card-check">
                        {selected.has(String(i)) ? '☑' : '☐'}
                      </div>
                      {card.imageSmall && (
                        <img src={card.imageSmall} alt={card.name} className="di-card-img" />
                      )}
                      <div className="di-card-info">
                        <div className="di-card-name">{card.name}</div>
                        <div className="di-card-meta">
                          <span className="di-card-set">{card.setCode?.toUpperCase()}</span>
                          <span className="di-card-rarity"
                                style={{ color: RARITY_COLOR[card.rarity] }}>
                            {card.rarity?.charAt(0).toUpperCase()}
                          </span>
                          {card.foil && <span className="di-card-foil">✨ Foil</span>}
                          <span className="di-match-strategy">via {card.matchStrategy}</span>
                        </div>
                        {card.importName !== card.name && (
                          <div className="di-import-name">Imported as: {card.importName}</div>
                        )}
                      </div>
                      <div className="di-card-qty">×{card.quantity}</div>
                      {card.price != null && (
                        <div className="di-card-price">${card.price.toFixed(2)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preview.unmatched.length > 0 && (
              <div className="di-section">
                <div className="di-section-label">❌ Unmatched — will not be imported</div>
                <div className="di-unmatched-list">
                  {preview.unmatched.map((row, i) => (
                    <div key={i} className="di-unmatched-row">
                      <span className="di-unmatched-name">{row.importName}</span>
                      {row.setCode && (
                        <span className="di-unmatched-set">
                          {row.setCode.toUpperCase()} #{row.cardNumber}
                        </span>
                      )}
                      <span className="di-unmatched-reason">{row.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && <div className="di-error">{error}</div>}

            <div className="di-actions">
              <button className="di-btn-cancel" onClick={() => setStep('input')}>← Back</button>
              <button className="di-btn-primary" onClick={handleConfirm}
                      disabled={loading || selected.size === 0}>
                {loading ? 'Importing…'
                  : `Import ${selected.size} card${selected.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: DONE ── */}
        {step === 'done' && result && (
          <div className="di-body di-done">
            <div className="di-done-icon">✅</div>
            <div className="di-done-title">Import Complete</div>
            <div className="di-done-stats">
              <span>{result.added} card{result.added !== 1 ? 's' : ''} added to {deckName}</span>
            </div>
            <button className="di-btn-primary" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
